import { useMutation } from '@apollo/client';
import { Button, Icon, Loading, Stack, Typography, colors } from '@ui-components/neo.tax';
import { ReactComponent as ChevronDownIcon } from '@ui-components/neo.tax/src/assets/icons/chevron-down.svg';
import * as Ariakit from 'ariakit';
import { ReactElement } from 'react';

import { useRdProjects, RdProject } from '@neo.tax/rd/hooks';
import {
  MOVE_TICKETS_TO_RD_PROJECT,
  MoveTicketsToRdProjectMutation,
  MoveTicketsToRdProjectMutationVariables,
  evictCache,
  useKeywordSearch,
  Menu,
  MenuItem,
} from '@neo.tax/shared';

import { MenuWrapper, ProjectList, SearchWrapper, StyledKeywordSearch } from './rd-tickets-pane.styled';

type MoveRdTicketsButtonProps = {
  /** The IDs of the Tickets to move. */
  ticketIds: Set<string>;
  /**
   * The ID of the Project to move from. This Project's Tickets will be
   * refreshed after the move
   */
  fromRdProjectId: string;
  /** The actual count of tickets that will be moved (used for display) */
  ticketCount: number;
  /** Optional grouping ticket IDs for bulk moves */
  groupingTicketIds: string[];
  /** Optional grouping collection IDs for bulk moves */
  groupingCollectionIds: string[];
  /** Whether to include tickets with no parent grouping */
  includeNoParentTickets?: boolean;
  /** Callback to be called when the tickets are moved */
  onCompleted: () => void;
};

const MOVE_RD_TICKETS_REQUEST_KEY = 'MOVE_RD_TICKETS_REQUEST_KEY';

export const MoveRdTicketsButton = ({
  fromRdProjectId,
  groupingCollectionIds,
  groupingTicketIds,
  includeNoParentTickets = false,
  onCompleted,
  ticketCount,
  ticketIds: selectedTicketIds,
}: MoveRdTicketsButtonProps): ReactElement => {
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const menuState = Ariakit.useMenuState({ gutter: 1 });
  const { loading: loadingRdProjects, rdProjects } = useRdProjects({ searchKeyword, fetchPolicy: 'no-cache' });

  const [moveTicketsToRdProject, { loading: loadingMoveTicketsToRdProject }] = useMutation<
    MoveTicketsToRdProjectMutation,
    MoveTicketsToRdProjectMutationVariables
  >(MOVE_TICKETS_TO_RD_PROJECT);

  const createOnClickHandler =
    (toRdProject: RdProject): (() => Promise<void>) =>
    async (): Promise<void> => {
      await moveTicketsToRdProject({
        variables: {
          rdProjectId: toRdProject.id,
          input: {
            ticketingProviderTicketIds: [...selectedTicketIds],
            groupingTicketingProviderTicketIds: groupingTicketIds,
            groupingTicketingProviderCollectionIds: groupingCollectionIds,
            includeNoParentTickets,
            sourceRdProjectId: fromRdProjectId,
          },
        },
        onCompleted,
        update: (cache) => {
          // Evict caches to refresh data after moving tickets
          // Note: getRdProjects is safe to evict now because the filtered query in this component
          // uses 'no-cache' and won't pollute the main table's cache
          evictCache(cache, [
            'getRdProjectTeamMembers',
            'getRdProjectTpTickets',
            'getRdProjects',
            'getRdProjectGroupingTicketsOrCollections',
          ]);

          // Update the ticket counts immediately so they don't flicker
          cache.modify({
            id: `RdProject:${toRdProject.id}`,
            fields: {
              ticketCount: (cachedTicketCount: number) => cachedTicketCount + ticketCount,
            },
            broadcast: true,
          });
          cache.modify({
            id: `RdProject:${fromRdProjectId}`,
            fields: {
              ticketCount: (cachedTicketCount: number) => cachedTicketCount - ticketCount,
            },
            broadcast: true,
          });
        },
        context: {
          serializationKey: MOVE_RD_TICKETS_REQUEST_KEY,
          debounceKey: MOVE_RD_TICKETS_REQUEST_KEY,
          errorToast: {
            title: 'Failed to move tickets to the selected project',
            description:
              'An unexpected error happened while updating. Please try again and if this error persists, contact support.',
          },
          successToast: {
            title: `Tickets moved to ${toRdProject.name}`,
          },
        },
      });
    };

  return (
    <>
      <Button
        as={Ariakit.MenuButton}
        state={menuState}
        size="sm"
        variant="primary"
        rounded="regular"
        disabled={ticketCount === 0 || !rdProjects || loadingMoveTicketsToRdProject}
        style={{ margin: 0 }}
      >
        <Stack gap={2}>
          {loadingMoveTicketsToRdProject ? (
            'Moving...'
          ) : (
            <>
              Move <Icon size={4} node={<ChevronDownIcon />} />
            </>
          )}
        </Stack>
      </Button>
      <Menu state={menuState}>
        <MenuWrapper>
          <SearchWrapper>
            <StyledKeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
          </SearchWrapper>
          <ProjectList direction="column">
            {loadingRdProjects && <Loading height={160} />}
            {!rdProjects || rdProjects.length === 0 ? (
              <MenuItem>
                <Stack alignItems="center" justifyContent="center" fullHeight>
                  <Typography color={colors.gray[300]} fontSize="lg" fontWeight="semibold" textAlign="center">
                    No projects
                  </Typography>
                </Stack>
              </MenuItem>
            ) : (
              rdProjects.map(
                (toRdProject) =>
                  toRdProject.id !== fromRdProjectId && (
                    <MenuItem key={toRdProject.id} onClick={createOnClickHandler(toRdProject)}>
                      {toRdProject.name}
                    </MenuItem>
                  ),
              )
            )}
          </ProjectList>
        </MenuWrapper>
      </Menu>
    </>
  );
};
